import { ClientSettingsService } from './ClientSettingsService';
import { RequiredConfigs } from '../classes/RequiredConfig';
import { WidgetGroup } from '../interfaces/UserInterfaces';

import { Injectable, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router, Params } from '@angular/router';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';
import { Observable, Subscription } from 'rxjs/Rx';

import websocketConnect from 'rxjs-websockets';
import 'rxjs/add/operator/share';

import { UsageCounter } from '../interfaces/UserInterfaces';

import { BsModalService } from 'ngx-bootstrap/modal';
import { BsModalRef } from 'ngx-bootstrap/modal/bs-modal-ref.service';

import {
    DataConsumerConfiguration,
    ManagedDataReply,
    WidgetConfiguration,
    DataPolicy
} from '../interfaces/WidgetInterfaces';

import { WebPlatformApiService } from './WebPlatformApiService';
import { AppStateService } from './AppStateService';
import {
    AppDisplayState,
    DataSet,
    SideNavWidgetConfig,
    DataRequestReply,
    DataRequestError,
    WebSocketData
} from '../interfaces/DisplayInterfaces';

import { Dictionary, UtilityFunctions } from '../classes/UtilityFunctions';
import { ObjectDiffFinderService } from './ObjectDiffFinderService';
import { strictEqual } from 'assert';

@Injectable()
export class DisplayStateService {

    /**
     * There are two ways that the display can be changed - query parameters or user clicks and input.  All changes are processed
     * through the same pipeline - turned into parameters, passed through the router navigate() method, and then processed as
     * new parameters. There is a problem where the navigate() method wont raise an event to listen to if only certain parameters
     * change, so this is a flag that lets the logic bypass handling post navigate() event so that it's only fired if the user
     * actually changes the url query parameters in the browser
     */
    public ignoreRouting = false;

    private _ready = false;
    // Used by various components to be notified when certain state changes.  See events class below
    public AppStateChanged: BehaviorSubject<string>;
    public MessageHouse: Subject<Params>;
    public DataAvailable: Subject<DataRequestReply>;
    public DataError: Subject<DataRequestError>;
    public Toast: Subject<string>;
    public SuccessToast: Subject<string>;
    public WarnToast: Subject<string>;
    public ErrorToast: Subject<string>;
    public modalRef: BsModalRef;
    public widgetContainer: TemplateRef<any>;
    // Entire display app state stored in this object
    appState: AppDisplayState = new AppDisplayState();

    qpSubscription: Subscription = undefined;

    constructor(private webPlatformApiService: WebPlatformApiService, private appStateService: AppStateService,
        private route: ActivatedRoute, private router: Router, private clientSettingsService: ClientSettingsService,
        public modalService: BsModalService, private objectDiffFinderService: ObjectDiffFinderService) {
        this.AppStateChanged = new BehaviorSubject<string>('initial');
        this.MessageHouse = new Subject<Params>();
        this.DataAvailable = new Subject<DataRequestReply>();
        this.DataError = new Subject<DataRequestError>();
        this.Toast = new Subject<string>();
        this.SuccessToast = new Subject<string>();
        this.WarnToast = new Subject<string>();
        this.ErrorToast = new Subject<string>();


        this.AppStateChanged.subscribe(x => {
            if (x === Events.USER_CHANGED && this.appState.current.user !== undefined) {
                this.GetPreferredSettingsUser();
                this.LoadWidgetConfigList();
                this.LoadSideNavConfigList();
                if (this.route && this.qpSubscription === undefined) {
                    this.qpSubscription = route.queryParams.subscribe(params => {
                        if (this.ignoreRouting) {
                            this.ignoreRouting = false;
                            return;
                        }
                        this.ProcessParams(params);
                    });
                }
            }
            if (this.Ready() && !this._ready) {
                this._ready = true;
                this.AppStateChanged.next(Events.APPLICATION_READY);
            }
        });
    }

    Log(logLevel: number, message: string, params?: Params) {
        UtilityFunctions.Log(logLevel, message, this.clientSettingsService, this.appStateService, params);

    }

    SetWidgetPreferencesObject(uniqueWidgetKey: string, value: any) {
        this.SetWidgetPreferencesString(uniqueWidgetKey, JSON.stringify(value));
    }

    SetWidgetPreferencesString(uniqueWidgetKey: string, value: string) {
        if (this.appState.current.userStatistics) {
            if (!this.appState.current.userStatistics.widgetPreferences) {
                this.appState.current.userStatistics.widgetPreferences = [];
            }
            const item = this.appState.current.userStatistics.widgetPreferences.find(x => x.uniqueWidgetKey === uniqueWidgetKey);
            if (item) {
                item.preferences = value;
            } else {
                this.appState.current.userStatistics.widgetPreferences.push(
                    { uniqueWidgetKey: uniqueWidgetKey, preferences: value });
            }

            this.SaveUserStats();
        }
    }

    GetWidgetPreferencesObject(uniqueWidgetKey: string): any  {
        const stringVersion = this.GetWidgetPreferencesString(uniqueWidgetKey);
        if (stringVersion) {
            return JSON.parse(stringVersion);
        } else {
            return undefined;
        }
    }

    GetWidgetPreferencesString(uniqueWidgetKey: string): string {
        if (this.appState.current.userStatistics) {
            if (this.appState.current.userStatistics.widgetPreferences) {
                const item = this.appState.current.userStatistics.widgetPreferences.find(x => x.uniqueWidgetKey === uniqueWidgetKey);
                if (item) {
                    return item.preferences;
                }
            }
        }

        if (this.appState.current.preferredSettings) {
            if (this.appState.current.preferredSettings.widgetPreferences) {
                const item = this.appState.current.preferredSettings.widgetPreferences.find(x => x.uniqueWidgetKey === uniqueWidgetKey);
                if (item) {
                    return item.preferences;
                }
            }
        }

        return undefined;
    }

    GetPreferredSettingsUser() {
        this.webPlatformApiService.GetUserStats('preferredSettings').subscribe(preferredStats => {
            this.appState.current.preferredSettings = preferredStats;
            this.AppStateChanged.next(Events.PREFERRED_SETTINGS_CHANGED);
        });
    }

    GetAllPreferredSettings(): WidgetGroup[] {
        if (this.appState.current.preferredSettings &&
            this.appState.current.preferredSettings.widgetRegistration &&
            this.appState.current.preferredSettings.widgetRegistration.groupedWidgets) {
            return this.appState.current.preferredSettings.widgetRegistration.groupedWidgets;
        } else {
            return [];
        }
    }

    GetPreferredSettings(contextKey: string): WidgetGroup[] {
        return this.GetAllPreferredSettings()
            .filter(gw => gw.contextKey === contextKey);
    }

    SetPreferredSettings(contextKey: string, newSettings: WidgetGroup[]) {
        this.webPlatformApiService.GetUserStats('preferredSettings').subscribe(preferredStats => {
            if (preferredStats) {
                if (!preferredStats.widgetRegistration) {
                    preferredStats.widgetRegistration = { excludedWidgets: [], groupedWidgets: [] };
                }
                if (!preferredStats.widgetRegistration.groupedWidgets) {
                    preferredStats.widgetRegistration.groupedWidgets = [];
                }
                const existingGroupedWidgets = preferredStats.widgetRegistration.groupedWidgets;
                const newGroupedWidgets = existingGroupedWidgets.filter(w => w.contextKey !== contextKey);
                newSettings
                    .filter(w => w.contextKey === contextKey)
                    .forEach(pg => { newGroupedWidgets.push(pg); });

                preferredStats.widgetRegistration.groupedWidgets = newGroupedWidgets;
                this.webPlatformApiService.UpsertUserStats(preferredStats).subscribe(x => { });
                this.appState.current.preferredSettings = preferredStats;
            }
        });
    }

    ClearLocalCache() {
        this.appState.cachedDataReplies = new Dictionary<string, ManagedDataReply>();
    }

    LoadWidgetConfigList() {
        UtilityFunctions.Log(3, `DisplayStateService: REQUEST WidgetConfigurationList from middle layer`,
            this.clientSettingsService, this.appStateService);
        this.webPlatformApiService.GetConfig<WidgetConfiguration[]>('WidgetConfigurationList').subscribe(
            widgetConfigList => {
                UtilityFunctions.Log(3, `DisplayStateService: RECEIVE WidgetConfigurationList from middle layer`,
                    this.clientSettingsService, this.appStateService);
                this.appState.widgetConfigList = widgetConfigList;
                this.AddRequiredWidgets();
                this.handleSideNavSetup();
                this.AppStateChanged.next(Events.WIDGET_CONFIG_LIST_CHANGED);
            },
            error => {
                this.appState.widgetConfigList = RequiredConfigs.Widgets();
                this.appStateService.appendLog('LoadWidgetConfigList ERROR: ' + JSON.stringify(error), 1);
            }
        );
    }

    public AddRequiredWidgets() {
        this.appState.widgetConfigList = this.AddRequiredWidgetsTo(this.appState.widgetConfigList);
    }

    public AddRequiredWidgetsTo(existing: WidgetConfiguration[]): WidgetConfiguration[] {
        const requiredWidgetNames = RequiredConfigs.Widgets().map(wc => wc.name);
        const filteredWidgets = existing.filter(wc => requiredWidgetNames.findIndex(r => r === wc.name) === -1);
        // merge requried configs with mongo DB values (so Mongo DB admins can still change in MongoDB as needed)
        const RequiredConfigsMerge = RequiredConfigs.Widgets().map(w => {
            const match = existing.find(e => e.name === w.name);
            if (match) {
                Object.assign(w, match);
            }
            return w;
        }) 
        return RequiredConfigsMerge.concat(filteredWidgets);
    }

    AddRequiredSideNavTo(existing: SideNavWidgetConfig) {
        const requiredSideNavs = RequiredConfigs.SideNavNameValues();
        existing.widgetNameList =
            existing.widgetNameList.filter(nv => requiredSideNavs.findIndex(r => r.name === nv.name) === -1);

        existing.widgetNameList = requiredSideNavs.concat(existing.widgetNameList);
    }

    // Called when user picks a side nav configuration but the data necessary to display in the side nav hasn't been
    // retrieved from the eng backend
    LoadSideNavConfigList() {
        UtilityFunctions.Log(3, `DisplayStateService: REQUEST SideNavWidgetConfigList from middle layer`,
            this.clientSettingsService, this.appStateService);
        this.webPlatformApiService.GetConfig<SideNavWidgetConfig>('SideNavWidgetConfigList').subscribe(
            sideNavWidgetConfig => {
                UtilityFunctions.Log(3, `DisplayStateService: RECEIVE SideNavWidgetConfigList from middle layer`,
                    this.clientSettingsService, this.appStateService);
                this.AddRequiredSideNavTo(sideNavWidgetConfig);
                this.appState.sideNavWidgetConfig = sideNavWidgetConfig;
                this.handleSideNavSetup();
            },
            error => this.appStateService.appendLog('LoadSideNavConfigList ERROR: ' + JSON.stringify(error), 1)
        );
    }

    private handleSideNavSetup() {
        // TODO: filter side nav names based on if they are excluded for user, and add logic to recreate the list when user
        // changes any registration of widgets.
        const readyToSetName =
            this.appState.sideNavWidgetConfig !== undefined &&
            this.appState.sideNavWidgetConfig.widgetNameList.length > 0 &&
            this.appState.widgetConfigList !== undefined &&
            this.appState.widgetConfigList.length > 0;

        if (readyToSetName) {
            const nv = this.appState.sideNavWidgetConfig.widgetNameList.find(s =>
                s.value === this.appState.current.sideNav.displayName);
            if (this.appState.current.sideNav.displayName === '' || nv === undefined) {
                this.appState.current.sideNav.displayName = this.appState.sideNavWidgetConfig.widgetNameList[0].value;
                this.appState.current.sideNav.widgetName = this.appState.sideNavWidgetConfig.widgetNameList[0].name;
            } else {
                this.appState.current.sideNav.widgetName = nv.name;
            }
            this.AppStateChanged.next(Events.SIDE_NAV_CHANGED);
        }
    }

    private setSideNav(name: string) {

        if (name === '') { return; }
        this.appState.current.sideNav.displayName = name;
        this.handleSideNavSetup();
    }
    SetWidgetInternalSideNav(name: string, componentType: string) {
        // exposed to widget onInitialize to allow a widget to dynamically insert it's own temporary side nav
        // to be used / available only while the widget is open (makes most sense for widget as an app use case)
        // a few conditional checks in case this gets run multiple times, only and and trigger changing the sidenav once
        if (this.appState.current.sideNav.displayName !== name) {
            const newSideNav = {name: name, value: name, WidgetInternalSideNav: true};
            const wcNames = this.appState.widgetConfigList.map(wc => wc.name);
            if (wcNames.indexOf(name) === -1) {
            this.appState.sideNavWidgetConfig.widgetNameList.push(newSideNav);
                const etm: WidgetConfiguration = {
                    'name': name,
                    'description': 'youll never see this',
                    'supportMessage': 'youll never see this',
                    'componentType': componentType,
                    'config': {},
                    'dataConsumerConfiguration': {
                        'dataPolicy': ''
                    },
                    'contextMatchingRules': []
                };
                this.appState.widgetConfigList.push(etm);

                this.appState.current.sideNav = {
                    widgetName: newSideNav.name,
                    displayName: newSideNav.name,
                    filter: newSideNav.name,
                    displayState: 'show'
                };
                // setTimeout is a quick fix for ExpressionChangedAfterItHasBeenCheckedError
                setTimeout(() => {
                    this.RouteSideNav(newSideNav.name);
                    this.RouteSideNavFilter('');
                }, 0);
            }
        }
    }
    RemoveWidgetsInternalSideNav() {
        const wisn = this.appState.sideNavWidgetConfig.widgetNameList.filter(sn => {
            return sn.WidgetInternalSideNav;
        });
        if (wisn.length) {
            const isnName = wisn[0].name;
            const snwcIx = this.appState.sideNavWidgetConfig.widgetNameList.map(wnl => wnl.name).indexOf(isnName);
            this.appState.sideNavWidgetConfig.widgetNameList.splice(snwcIx, 1);
            const wclIx = this.appState.widgetConfigList.map(wcl => wcl.name).indexOf(isnName);
            this.appState.widgetConfigList.splice(wclIx, 1);
            // if its currently the active sidenav then we need to change it..
            if (this.appState.current.sideNav.displayName === isnName) {
                const firstSN = this.appState.sideNavWidgetConfig.widgetNameList[0];
                this.appState.current.sideNav = {
                    widgetName: firstSN.name,
                    displayName: firstSN.name,
                    filter: '',
                    displayState: 'show'
                };
            }
        }
    }

    EnablePrintMode(printMode: boolean) {
        this.appState.current.isPrintMode = printMode;
        this.AppStateChanged.next(Events.PRINT_PREVIEW_MODE);
    };

    ExistingParams(): Params {

        const existing: Params = [];

        existing[UrlConstants.WIDGETS_PER_ROW] = this.appState.current.perRow;

        existing[UrlConstants.SIDENAV_SHOW] = this.appState.current.sideNav.displayState;
        if (this.appState.current.sideNav.displayName) {
            existing[UrlConstants.SIDENAV_NAME] = this.appState.current.sideNav.displayName;
        }
        existing[UrlConstants.WIDGETS_PER_ROW] = this.appState.current.perRow;
        existing[UrlConstants.SIDENAV_FILTER] = this.appState.current.sideNav.filter;

        for (const key of Object.keys(this.appState.current.context)) {
            existing[`c.${key}`] = this.appState.current.context[key];
        }
        for (const key of Object.keys(this.appState.current.filters)) {
            existing[`f.${key}`] = this.appState.current.filters[key];
        }
        for (const key of Object.keys(this.appState.current.sorts)) {
            existing[`s.${key}`] = this.appState.current.sorts[key];
        }

        return existing;
    }

    RouteWidgetsPerRow(count: string) {
        const params = this.ExistingParams();
        params[UrlConstants.WIDGETS_PER_ROW] = count;
        this.Route(params);
    }

    RouteSideNavFilter(filter: string) {
        const params = this.ExistingParams();
        params[UrlConstants.SIDENAV_FILTER] = filter;
        this.Route(params);
    }

    // Route* methods are called by components when the user does something that elicits a change in state
    RouteSideNav(name: string) {
        const params = this.ExistingParams();
        params[UrlConstants.SIDENAV_NAME] = name;
        this.Route(params);
    }

    SetFullScreen(fullScreen: boolean) {
        const params = this.ExistingParams();
        const showHide = fullScreen ? 'hide' : 'show';
        params[UrlConstants.SIDENAV_SHOW] = showHide;
        params[UrlConstants.BREADCRUMB_SHOW] = showHide;
        this.Route(params);

    }
    SetSideNavVisible(visible: boolean) {
        const params = this.ExistingParams();
        const showHide = visible ? 'show' : 'hide';
        params[UrlConstants.SIDENAV_SHOW] = showHide;
        this.Route(params);
    }


    ToggleNavDisplay() {
        if (this.appState.current.sideNav.displayState === 'show') {
            this.RouteSideNavDisplay('hide');
        } else {
            this.RouteSideNavDisplay('show');
        }
    }

    ToggleBreadcrumb() {
        if (this.appState.current.breadcrumb === 'show') {
            this.RouteBreadcrumb('hide');
        } else {
            this.RouteBreadcrumb('show');
        }
    }
    RouteSideNavDisplay(display: string) {
        const params = this.ExistingParams();
        params[UrlConstants.SIDENAV_SHOW] = display;
        this.Route(params);
    }
    RouteBreadcrumb(display: string) {
        const params = this.ExistingParams();
        params[UrlConstants.BREADCRUMB_SHOW] = display;
        this.Route(params);
    }

    RemoveSort(keyToRemove: string) {
        const params = this.ExistingParams();
        delete (params[`s.${keyToRemove}`]);
        this.Route(params);
    }

    UserStatsIncrement(usageCounterList: UsageCounter[], key: string, autosave = true) {
        let thisUsageCounter = usageCounterList.find(x => x.key === key);
        if (thisUsageCounter == null) {
            thisUsageCounter = { key: key, count: 0, lastUsage: '' };
            usageCounterList.push(thisUsageCounter);
        }
        thisUsageCounter.count++;
        thisUsageCounter.lastUsage = new Date().toLocaleString();
        if (autosave) {
            this.SaveUserStats();
        }
    }

    Ready(): boolean {

        return this.appState !== undefined &&
            this.appState.apiVersion !== undefined &&
            this.appState.current !== undefined &&
            this.appState.current.userStatistics !== undefined &&
            this.appState.current.preferredSettings !== undefined &&
            this.appState.widgetConfigList !== undefined &&
            this.appState.widgetConfigList.length > 0 &&
            this.appState.sideNavWidgetConfig !== undefined &&
            this.appState.sideNavWidgetConfig.widgetNameList !== undefined
    }

    SetUser(user: adal.User) {
        this.appState.current.user = user;
        this.appState.current.userStatistics = undefined;

        this.webPlatformApiService.GetUserStats(this.appState.current.user.userName.split('@')[0]).subscribe(uStats => {
            if (uStats.widgetRegistration && uStats.widgetRegistration.groupedWidgets) {
                uStats.widgetRegistration.groupedWidgets =
                    uStats.widgetRegistration.groupedWidgets.filter(gw => gw.contextKey !== null && gw.contextKey !== undefined);
            }
            this.appState.current.userStatistics = uStats;
            this.UserStatsIncrement(this.appState.current.userStatistics.applicationUsageStatistics, 'general');
            this.AppStateChanged.next(Events.USER_CHANGED);
        });
    }

    SetAppTitle(title: string) {
        // allow a widget to change the app title, revert back to the client setting appName when the widget is closed
        if (title) {
            this.appState.current.appTitle = title;
            this.AppStateChanged.next(Events.TITLE_CHANGED)
        } else {
            this.appState.current.appTitle = this.clientSettingsService.ApplicationName;
            this.AppStateChanged.next(Events.TITLE_CHANGED)
        }
    }

    SaveUserStats() {
        if (this.appState.current.userStatistics !== undefined) {
            this.webPlatformApiService.UpsertUserStats(this.appState.current.userStatistics).subscribe(x => { });
        }
    }

    SetSorts(Sorts: Params) {
        const params = this.ExistingParams();
        const newParams: Params = [];

        Object.keys(params).forEach(k => {
            if (!k.startsWith('s.')) {
                newParams[k] = params[k];
            }
        });
        Object.keys(Sorts).forEach(x => { newParams[`s.${x}`] = Sorts[x]; });
        this.Route(newParams);
    }

    AddSorts(newSorts: Params) {
        const params = this.ExistingParams();
        Object.keys(newSorts).forEach(k => { params[`s.${k}`] = newSorts[k]; });
        this.Route(params);
    }

    AddSort(name: string, value: string) {
        const params = this.ExistingParams();
        params[`s.${name}`] = value;
        this.Route(params);
    }

    RemoveFilter(keyToRemove: string) {
        const params = this.ExistingParams();
        delete (params[`f.${keyToRemove}`]);
        this.Route(params);
    }

    SetFilters(filters: Params) {
        const params = this.ExistingParams();

        Object.keys(this.appState.current.filters).forEach(x => {
            delete (params[`f.${x}`]);
        });
        Object.keys(filters).forEach(x => { params[`f.${x}`] = filters[x]; });
        this.Route(params);
    }

    AddFilters(newFilters: Params) {
        const params = this.ExistingParams();
        Object.keys(newFilters).forEach(k => { params[`f.${k}`] = newFilters[k]; });
        this.Route(params);
    }

    AddFilter(name: string, value: string) {
        const params = this.ExistingParams();
        params[`f.${name}`] = value;
        this.Route(params);
    }

    RemoveContext(keyToRemove: string) {
        const params = this.ExistingParams();
        delete (params[`c.${keyToRemove}`]);
        const chKeyIx = this.appState.current.contextHist.map(ch => ch.key).indexOf(keyToRemove);
        if (chKeyIx !== -1) {
            this.appState.current.contextHist.splice(chKeyIx, 1);
        }
        this.Route(params);
    }

    SetContext(newContext: Params) {
        this.DoContextHist(newContext);
        // UtilityFunctions.DebugLog('SetContext new', newContext);
        const params = this.ExistingParams();
        const newParams: Params = [];

        Object.keys(params).forEach(k => {
            if (!k.startsWith('c.')) {
                newParams[k] = params[k];
            }
        });

        // UtilityFunctions.DebugLog('SetContext -> existing params after removing current context', newParams);
        Object.keys(newContext).forEach(x => { newParams[`c.${x}`] = newContext[x]; });
        this.Route(newParams);
    }

    AddContextList(newContext: Params) {
        this.DoContextHist(newContext);
        // UtilityFunctions.DebugLog('AddContextList new', newContext);
        const params = this.ExistingParams();

        Object.keys(newContext).forEach(x => { params[`c.${x}`] = newContext[x]; });
        // UtilityFunctions.DebugLog('AddContextList -> new params', params);
        this.Route(params);
    }

    AddContext(name: string, value: string) {
        let np: Params = {};
        np[name] = value;
        this.DoContextHist(np);
        const params = this.ExistingParams();
        // Object.keys(ctxt).forEach(x => { params[x] = ctxt[x]; });
        params[`c.${name}`] = value;
        this.Route(params);
    }

    DoContextHist(newContext: Params) {
        // update appState.current.contextHist based on the newContext
        // first find all context items from widgetconfig that support history..
        const histCtx = this.appState.widgetConfigList.reduce((p, wc) => {
            const cmrs = wc.contextMatchingRules.filter(cmr => {
                return cmr.historyItemsToKeep && cmr.historyItemsToKeep > 0;
            })
            p = p.concat(cmrs);
            return p;
        }, []);
        const histCtxKeys = histCtx.map(cmr => cmr.contextName);
        // then check if any of the newContext items are configured for hist..
        Object.keys(newContext).forEach(k => {
            if (histCtxKeys.indexOf(k) !== -1) {
                // find the prev value of this key.. (we shouldn't have changed the appState.current yet)
                const pv = this.appState.current.context[k];
                const historyItemsToKeep = histCtx[histCtxKeys.indexOf(k)].historyItemsToKeep;
                // populate appState.current.contextHist
                // contextHist?: {key:string, prevValues: string[]}[] = [];
                let ch = this.appState.current.contextHist.find(ch2 => ch2.key === k);
                if (ch) { // check if we already have some context hist for this key
                    // check if the value is already in the history..
                    if (ch.prevValues.indexOf(pv) === -1) {
                        ch.prevValues.push(pv);
                    }
                    if (ch.prevValues.length > historyItemsToKeep) {
                        this.appState.current.contextHist.pop();
                    }
                } else if (pv !== undefined) {
                    // else create a new contextHist item for this context key
                    this.appState.current.contextHist.push({key: k, prevValues: [pv]});
                }
            }
        });
    }

    public RouteFromQueryParamString(qpString: string) {
        const newParams: Params = [];

        qpString.split('&').forEach(x => {
            const parts = x.split('=');
            newParams[parts[0]] = parts[1];
        });
        this.Route(newParams);
    }

    // Handles all route initiated by the application components.  Takes all current settings, replaces any that were changed,
    // and triggers routing navigation
    public Route(newParams: Params) {
        // UtilityFunctions.DebugLog('Route: new', newParams);
        // UtilityFunctions.DebugLog('Route: existing', this.ExistingParams());
        // Trigger routing navigation - this ensures that all the state parameters show up in the url as query params
        this.ignoreRouting = true;

        if (this.router) {
            this.router.navigate([''], { queryParams: newParams });
        } else {
            UtilityFunctions.Log(
                1,
                'cannot route as this.router isnt valid',
                this.clientSettingsService,
                this.appStateService);
        }

        // Explicity process the new parameters since navigate() will change url query params but doesn't necessarily
        // raise the event we are listening to to catch new parameters
        this.ProcessParams(newParams);
    }

    // Generic handler of all changes to display state parameters.  Can be triggered by user changing query params in
    // the URL or by one of the Route* methods above.
    ProcessParams(queryParams: Params) {

        // UtilityFunctions.DebugLog(`DisplayStateService.ProcessParams queryParams`, queryParams);

        const context: Params = [];
        const filters: Params = [];
        const sorts: Params = [];
        let sideNavName = '';
        let _showSideNav = this.appState.current.sideNav.displayState;
        let _breadCrumb = this.appState.current.breadcrumb;

        // Split new params up
        for (const key of Object.keys(queryParams)) {
            if (key === UrlConstants.SIDENAV_NAME) {
                sideNavName = queryParams[key];
            } else if (key === UrlConstants.WIDGETS_PER_ROW) {
                this.SetWidthClass(queryParams[key]);
            } else if (key === UrlConstants.SIDENAV_FILTER) {
                this.SetSideNavFilter(queryParams[key]);
            } else if (key === UrlConstants.SIDENAV_SHOW) {
                _showSideNav = queryParams[key];
            } else if (key === UrlConstants.BREADCRUMB_SHOW) {
                _breadCrumb = queryParams[key];
            } else if (key.startsWith('f.')) {
                filters[key.substr(2)] = queryParams[key];
            } else if (key.startsWith('c.')) {
                context[key.substr(2)] = queryParams[key];
            } else if (key.startsWith('s.')) {
                sorts[key.substr(2)] = queryParams[key];
            }
        }

        this.setSideNav(sideNavName);
        if (this.appState.current.sideNav.displayState !== _showSideNav) {
            this.appState.current.sideNav.displayState = _showSideNav;
            this.AppStateChanged.next(Events.SIDE_NAV_DISPLAYED_CHANGED);
        }

        if (this.appState.current.breadcrumb !== _breadCrumb) {
            this.appState.current.breadcrumb = _breadCrumb;
            this.AppStateChanged.next(Events.BREADCRUMB_DISPLAYED_CHANGED);
        }

        if (this.differentParams(context, this.appState.current.context)) {
            this.appState.current.context = context;
            this.AppStateChanged.next(Events.CURRENT_CONTEXT_CHANGED);
        }

        if (this.differentParams(sorts, this.appState.current.sorts)) {
            this.appState.current.sorts = sorts;
            this.AppStateChanged.next(Events.CURRENT_SORTS_CHANGED);
        }

        if (this.differentParams(filters, this.appState.current.filters)) {
            this.appState.current.filters = filters;
            this.AppStateChanged.next(Events.CURRENT_FILTERS_CHANGED);
        }
    }

    SetWidthClass(perRow: string) {

        if (perRow !== this.appState.current.perRow) {
            this.appState.current.perRow = perRow;
            switch (perRow) {
                case '1': this.appState.current.widthClass = 'col-md-12'; break;
                case '2': this.appState.current.widthClass = 'col-md-6'; break;
                case '3': this.appState.current.widthClass = 'col-md-4'; break;
                case '4': this.appState.current.widthClass = 'col-md-3'; break;
            }

            this.AppStateChanged.next(Events.WIDTH_CLASS_CHANGED);
        }
    }

    // Called by anything that wants to change the side nav filter string, which is probably only the side nav component
    SetSideNavFilter(filter: string) {
        if (this.appState.current.sideNav.filter !== filter) {
            this.appState.current.sideNav.filter = filter;
            this.AppStateChanged.next(Events.SIDE_NAV_FILTER_CHANGED);
        }
    }



    // Returns true if anything in two lists or parameters are different
    private differentParams(a: Params, b: Params): boolean {
        const aString = UtilityFunctions.SortedParamsString(a);
        const bString = UtilityFunctions.SortedParamsString(b);
        return aString !== bString;
    }

    public GetDataConsumerConfigKey(dataConsumerConfig: DataConsumerConfiguration): string {
        const additionalParams: Params = this.GetParams(dataConsumerConfig);

        const paramString = UtilityFunctions.SortedParamsString(additionalParams);
        const returnKey = paramString ? dataConsumerConfig.dataPolicy + ':' + paramString : dataConsumerConfig.dataPolicy;
        // UtilityFunctions.DebugLog(`GetDataConsumerConfigKey: returning ${returnKey}`);
        return returnKey;
    }

    GetCustomHeaders(dataConsumerConfig: DataConsumerConfiguration): string {
        let headers = null;
        if (dataConsumerConfig.overrideCustomHeaders && dataConsumerConfig.overrideCustomHeaders.length > 0) {
            headers = JSON.stringify(dataConsumerConfig.overrideCustomHeaders);
        }
        return headers;
    }

    GetParams(dataConsumerConfig: DataConsumerConfiguration): Params {
        const additionalParams: Params = [];

        // Create context mapping rules - this means that current context values can be sent to the
        // middle layer as part of the query
        if (dataConsumerConfig.contextParameters !== undefined) {
            dataConsumerConfig.contextParameters.forEach(
                nv => {
                    const nameToSend = nv.value.length > 0 ? nv.value : nv.name;
                    additionalParams[`p.${nameToSend}`] = encodeURIComponent(this.appState.current.context[nv.name]);
                });
        }

        if (dataConsumerConfig.fixedParameters !== undefined) {
            dataConsumerConfig.fixedParameters.forEach(nv => {
                additionalParams[`p.${nv.name}`] = encodeURIComponent(nv.value);
            });

        }

        // TODO: Move the cache.* parameters out of here as they affect the cache key if used
        if (this.appState.current.cachePolicy === CacheConstants.NEVER) {
            additionalParams['cache.period'] = CacheConstants.NEVER;
        } else if (this.appState.current.cachePolicy === CacheConstants.PERMANENT) {
            additionalParams['cache.period'] = CacheConstants.PERMANENT;
        } else if (dataConsumerConfig.overrideCacheRules !== undefined && dataConsumerConfig.overrideCacheRules.period) {
            // tslint:disable-next-line:max-line-length
            if (dataConsumerConfig.overrideCacheRules.frequency !== undefined) { additionalParams['cache.frequency'] = dataConsumerConfig.overrideCacheRules.frequency; }
            // tslint:disable-next-line:max-line-length
            if (dataConsumerConfig.overrideCacheRules.period !== undefined) { additionalParams['cache.period'] = dataConsumerConfig.overrideCacheRules.period; }
            // tslint:disable-next-line:max-line-length
            if (dataConsumerConfig.overrideCacheRules.targetDay !== undefined) { additionalParams['cache.targetDay'] = dataConsumerConfig.overrideCacheRules.targetDay; }
            // tslint:disable-next-line:max-line-length
            if (dataConsumerConfig.overrideCacheRules.targetHour !== undefined) { additionalParams['cache.targetHour'] = dataConsumerConfig.overrideCacheRules.targetHour; }
            // tslint:disable-next-line:max-line-length
            if (dataConsumerConfig.overrideCacheRules.targetMinute !== undefined) { additionalParams['cache.targetMinute'] = dataConsumerConfig.overrideCacheRules.targetMinute; }
        }
        // Filtering?
        if (dataConsumerConfig.filterConfiguration !== undefined) {
            dataConsumerConfig.filterConfiguration.filter(nv => nv.serverSide === true).forEach(
                nv => {
                    let valueToUse: string = undefined;

                    if (nv.contextValueName !== undefined && nv.contextValueName in this.appState.current.context) {
                        valueToUse = this.appState.current.context[nv.contextValueName];
                    } else if (nv.value !== undefined && nv.value.length > 0) {
                        valueToUse = nv.value;
                    }

                    if (valueToUse !== undefined && nv.value.length > 0) {
                        additionalParams[`f.${nv.column}`] =
                            nv.operator !== undefined ?
                                `${nv.operator}.${valueToUse}` :
                                `${valueToUse}`;
                    }
                });
        }

        // Grouping?  Example query params:
        // g.TRAVELER_STEP,IS_ANOMALY
        //  =
        // run_id_count.sum.idCount,run_id_count.count.idCount,run_id_count.mean.idMean
        if (dataConsumerConfig.dataGrouping && dataConsumerConfig.dataGrouping.serverSide) {
            const paramName = 'g.' +
                dataConsumerConfig.dataGrouping.groupBy.join(',');
            const paramValue =
                dataConsumerConfig.dataGrouping.aggregations.map(a => `${a.name}.${a.operation}.${a.rename}`);
            additionalParams[paramName] = paramValue;
        }

        // Pivoting?
        // Example query params: pvt.IS_ANOMALY=run_id_count.sum
        if (dataConsumerConfig.pivot && dataConsumerConfig.pivot.name && dataConsumerConfig.pivot.operation) {
            const paramName = 'pvt.' +
                dataConsumerConfig.pivot.name;
            const paramValue = `${dataConsumerConfig.pivot.rename}.${dataConsumerConfig.pivot.operation}`;
            dataConsumerConfig.dataGrouping.aggregations.map(a => `${a.name}.${a.operation}.${a.rename}`);
            additionalParams[paramName] = paramValue;
        }
        // UtilityFunctions.DebugLog(`GetDataConsumerConfigKey: dataConsumerConfig = ${JSON.stringify(dataConsumerConfig, null , 2)} `);
        // UtilityFunctions.DebugLog(`GetParams: context is ${UtilityFunctions.SortedParamsString(this.appState.current.context)}`);
        // UtilityFunctions.DebugLog(`GetParams: returning ${UtilityFunctions.SortedParamsString(additionalParams)}`);
        return additionalParams;
    }

    RequestWidgetData(config: WidgetConfiguration): DataRequestReply {
        return this.RequestData(config.name, config.dataConsumerConfiguration);
    }

    UpdateWidgetConfigColumns(widgetName: string, dataConsumerConfig: DataConsumerConfiguration, reply: ManagedDataReply) {
        let matchingWC = this.appState.widgetConfigList.find(wc => wc.name === widgetName);

        const asDataSet = <DataSet>reply.CacheData.Payload;
        if (asDataSet.columns !== undefined && dataConsumerConfig.columns === undefined) {
            if (matchingWC !== undefined) {
                if (matchingWC.dataConsumerConfiguration) {
                    matchingWC.dataConsumerConfiguration.columns = asDataSet.columns;
                    this.webPlatformApiService.UpdateConfiguration(
                        'WidgetConfigurationList',
                        this.appState.widgetConfigList).subscribe(x => { });
                }
            }
            dataConsumerConfig.columns = asDataSet.columns;
        }
    }
    RequestData(widgetName: string, dataConsumerConfig: DataConsumerConfiguration): DataRequestReply {
        const key = this.GetDataConsumerConfigKey(dataConsumerConfig);
        if (dataConsumerConfig.directUri && dataConsumerConfig.directUri.length) {
            if (this.appState.pendingDataReplies.findIndex(s => s === key) === -1) {
                this.appState.pendingDataReplies.push(key);
                this.Log(4, `querying generic web api service key ${key}`);
                this.appStateService.appendLog(`querying web api service key ${key}`, 2);
                const uri = this.UpdateUriWithContextVars(dataConsumerConfig.directUri,
                    this.appState.current.context, dataConsumerConfig.dataPolicy);
                const dataReply$ = this.webPlatformApiService.GetGenericDataReply(
                    uri, dataConsumerConfig, null, this.appState.current.xMtAdfsOpenid); // httpMethod should be get or delete here..

                dataReply$.subscribe(datareply => {
                    this.Log(4, `received data reply for key ${key}`);
                    this.Log(5, `pending data replies: ${JSON.stringify(this.appState.pendingDataReplies)}`);
                    const idx = this.appState.pendingDataReplies.findIndex(s => s === key);
                    if (idx > -1) {
                        this.Log(4, `reply for key ${key} was received and removed from pendingDataReplies`);
                        this.appState.pendingDataReplies.splice(idx, 1);
                    } else {
                        this.Log(3, `!!! reply for key ${key} was received but NOT in pendingDataReplies`);
                    }
                    // construct DataRequestReply with rawReply instead of ImmediateManagedDataReply
                    this.DataAvailable.next(new DataRequestReply(key, true, null, datareply));
                }, error => {
                    this.appState.pendingDataReplies = this.appState.pendingDataReplies.filter(dr => dr !== key);
                    this.DataError.next(new DataRequestError(widgetName, dataConsumerConfig.dataPolicy, key, error));
                });
            } else {
                this.Log(4, `pending request for web api service key ${key} already exists`);
            }
        } else {

            if (dataConsumerConfig.fixedData !== undefined && dataConsumerConfig.fixedData.columns && dataConsumerConfig.fixedData.data) {
                return new DataRequestReply(
                    `${widgetName}.fixedData`, true,
                    {
                        AppliedDataPolicy: undefined,
                        CacheData: {
                            CacheDate: '',
                            key: '',
                            MinutesRemaining: 999,
                            Payload: dataConsumerConfig.fixedData,
                            RetrievedFromCache: false
                        }

                    });
            }
            if (dataConsumerConfig.dataPolicy === undefined || dataConsumerConfig.dataPolicy.length === 0) {
                return;
            }
            if (this.appState.cachedDataReplies.containsKey(key)) {

                this.Log(4, `using locally cached managed data set for key ${key}`);
                const datareply = this.appState.cachedDataReplies.get(key);
                this.UpdateWidgetConfigColumns(widgetName, dataConsumerConfig, datareply);

                return new DataRequestReply(key, true, this.appState.cachedDataReplies.get(key));
            } else {

                if (this.appState.pendingDataReplies.findIndex(s => s === key) === -1) {
                    this.appState.pendingDataReplies.push(key);
                    this.Log(4, `querying eng web api service key ${key}`);
                    this.appStateService.appendLog(`querying eng web api service key ${key}`, 2);
                    const dataReply$ = this.webPlatformApiService.GetManagedDataReply(
                        dataConsumerConfig.dataPolicy,
                        this.GetParams(dataConsumerConfig),
                        this.GetCustomHeaders(dataConsumerConfig)
                    );

                    dataReply$.subscribe(datareply => {
                        this.Log(4, `received data reply for key ${key}`);
                        this.Log(5, `pending data replies: ${JSON.stringify(this.appState.pendingDataReplies)}`);
                        const idx = this.appState.pendingDataReplies.findIndex(s => s === key);
                        if (idx > -1) {
                            this.Log(4, `reply for key ${key} was received and removed from pendingDataReplies`);
                            this.appState.pendingDataReplies.splice(idx, 1);
                        } else {
                            this.Log(3, `!!! reply for key ${key} was received but NOT in pendingDataReplies`);
                        }

                        this.appState.cachedDataReplies.upsert(key, datareply);
                        this.UpdateWidgetConfigColumns(widgetName, dataConsumerConfig, datareply);

                        this.DataAvailable.next(new DataRequestReply(key, true, datareply));
                    },
                        error => {
                            this.appState.pendingDataReplies = this.appState.pendingDataReplies.filter(dr => dr !== key);
                            this.DataError.next(new DataRequestError(widgetName, dataConsumerConfig.dataPolicy, key, error));
                        });
                } else {
                    this.DataAvailable.subscribe(reply => {
                        if (reply.RequestKey === key) {
                            this.UpdateWidgetConfigColumns(widgetName, dataConsumerConfig, reply.ImmediateManagedDataReply);
                        }
                    });
                    this.Log(4, `pending request for web api service key ${key} already exists`);
                }


                return new DataRequestReply(key, false, undefined);
            }
        }
    }
    SendData(widgetName: string, dataConsumerConfig: DataConsumerConfiguration, dataToSend: any): DataRequestReply {
        // this would be used for put / post routes - dataToSend is http body to send
        const key = this.GetDataConsumerConfigKey(dataConsumerConfig);
        if (dataConsumerConfig.directUri && dataConsumerConfig.directUri.length && dataConsumerConfig.httpMethod) {
            this.Log(4, `sending data to generic web api service key ${key}`);
            this.appStateService.appendLog(`querying web api service key ${key}`, 2);
            const uri = this.UpdateUriWithContextVars(dataConsumerConfig.directUri,
                this.appState.current.context, dataConsumerConfig.dataPolicy);
            const dataReply$ = this.webPlatformApiService.GetGenericDataReply(
                uri, dataConsumerConfig, dataToSend, this.appState.current.xMtAdfsOpenid );
            dataReply$.subscribe(datareply => {
                this.Log(4, `received data reply for key ${key}`);
                // construct DataRequestReply with rawReply instead of ImmediateManagedDataReply
                this.DataAvailable.next(new DataRequestReply(key, true, null, datareply));
            }, error => {
                this.DataError.next(new DataRequestError(widgetName, dataConsumerConfig.dataPolicy, key, error));
            });
            return new DataRequestReply(key, false, undefined);
        }
    }
    GetWebSocketDataObj(dataConsumerConfig: DataConsumerConfiguration): WebSocketData {
        // first check for existance of the websocket already in the appState.current
        const wsKeys = this.appState.current.webSockets.map(ws => ws.WebSocketKey);
        const dataPolicyName = dataConsumerConfig.dataPolicy;
        if (wsKeys.indexOf(dataConsumerConfig.dataPolicy) !== -1) {
            return this.appState.current.webSockets[wsKeys.indexOf(dataPolicyName)];
        } else if (dataConsumerConfig.directUri && dataConsumerConfig.dataPolicy) {
            // dataPolicyName becomes the WebSocketKey
            const wsd = new WebSocketData(dataPolicyName);
            wsd.Messages = Observable.of('initial');
            wsd.ConnectionStatus = Observable.of('initial');
            // Using share() causes a single websocket to be created when the first
            // observer subscribes. This socket is shared with subsequent observers
            // and closed when the observer count falls to zero.
            // let inputStream: QueueingSubject<string>;
            const uri = this.UpdateUriWithContextVars(dataConsumerConfig.directUri,
                this.appState.current.context, dataConsumerConfig.dataPolicy);
            const { messages, connectionStatus } = websocketConnect( uri, wsd.InputStream );
            UtilityFunctions.Log(3, `Initializing WebSocketData Object for
             ${dataPolicyName}`, this.clientSettingsService, this.appStateService);

            // If the websocket is not connected then the QueueingSubject will ensure
            // that messages are queued and delivered when the websocket reconnects.
            // A regular Subject can be used to discard messages sent when the websocket
            // is disconnected.
            wsd.ConnectionStatus = connectionStatus.share();
            wsd.Messages = messages.share();
            /* examples: these would go in the widget implementation..
            const wsd = this.displayStateService.GetWebSocketDataObj(this.DataConsumerConfig);
            wsd.InputStream.next('some message');
            wsd.ConnectionStatus.subscribe(numberConnected => {
                console.log('conn status', numberConnected);
            });
            wsd.Messages.subscribe((msg: string) => {
                try {
                    const parsed = JSON.parse(msg);
                } catch (error) {
                    console.log('msg received, error parsing', msg);
                }
                console.log('msg received', parsed);
            });
            */
            this.appState.current.webSockets.push(wsd);
            return wsd;
        }
    }
    UpdateUriWithContextVars(uri: string, context: object, dataPolicyName: string): string {
        let hasVars = false;
        let newUri = '';
        if (uri) {
            if (uri.indexOf('{') !== -1) {
                let urlParts = uri.split(/{([^}]*)}/g);
                urlParts.forEach(up => {
                    if (up.indexOf('/') === -1) { // other url parts will have slashes
                        let ctxKeys = Object.keys(context);
                        if (ctxKeys.indexOf(up) !== -1) {
                            hasVars = true;
                            let ctxKeyIndx = ctxKeys.indexOf(up);
                            // we found a matching context
                            let ctxVal = context[ctxKeys[ctxKeyIndx]];
                            newUri += ctxVal;
                        } else {
                            // we don't, something is wrong..
                            this.RaiseErrorToast('Missing Requred Context: ' + up + ' for data policy: ' + dataPolicyName);
                        }
                    } else {
                        newUri += up;
                    }
                });
            } else {
                newUri = uri;
            }
        }
        return newUri;
    }

    RaiseToast(message: string) {
        this.Toast.next(message);
    }
    RaiseSuccessToast(message: string) {
        this.SuccessToast.next(message);
    }
    RaiseWarnToast(message: string) {
        this.WarnToast.next(message);
    }
    RaiseErrorToast(message: string) {

        if (message && message.length > 120) {
            message = message.substr(0, 116) + '...';
        }
        this.ErrorToast.next(message);
    }

    // Shortcuts to commonly accessed parts of app state

    // this.DisplayStateService.appState.current.userStatistics.widgetRegistration.groupedWidgets

    GroupedWidgets(): WidgetGroup[] {
        if (this.appState && this.appState.current && this.appState.current.userStatistics &&
            this.appState.current.userStatistics.widgetRegistration &&
            this.appState.current.userStatistics.widgetRegistration.groupedWidgets) {
            return this.appState.current.userStatistics.widgetRegistration.groupedWidgets;
        } else {
            return undefined;
        }
    }

    OpenModal(template, customClass) {
        let config = {
            animated: true,
            keyboard: true,
            backdrop: true,
            ignoreBackdropClick: false
        };
        return this.modalService.show(template, Object.assign({}, config, { class: customClass }));
    }

    HideModal(template: BsModalRef) {
        template.hide();
    }

    GetJsonDiffHtml(db, user) {
        let diff = this.objectDiffFinderService.diffObject(db, user, undefined, true);
        return this.objectDiffFinderService.formatToJsonXMLString(diff);
    }

    IsByDefaultSideNav(name): boolean {
        return RequiredConfigs.SideNavNameValues().find(data => data.name === name) ? true : false;
    }

    IsByDefaultWidget(name): boolean {
        return RequiredConfigs.Widgets().find(data => data.name === name) ? true : false;
    }
}

// Basically constant strings used through this service and related components to specify what kind
// of state change occured.
export class Events {
    public static APPLICATION_READY = 'APPLICATION_READY';
    public static SIDE_NAV_CHANGED = 'SIDE_NAV_CHANGED';
    public static SIDE_NAV_DISPLAYED_CHANGED = 'SIDE_NAV_DISPLAYED_CHANGED';
    public static BREADCRUMB_DISPLAYED_CHANGED = 'BREADCRUMB_DISPLAYED_CHANGED';
    public static SIDE_NAV_FILTER_CHANGED = 'SIDE_NAV_FILTER_CHANGED';
    public static CURRENT_CONTEXT_CHANGED = 'CURRENT_CONTEXT_CHANGED';
    public static CURRENT_SORTS_CHANGED =  'CURRENT_SORTS_CHANGED';
    public static CURRENT_FILTERS_CHANGED = 'CURRENT_FILTERS_CHANGED';
    public static WIDGET_CONFIG_LIST_CHANGED = 'WIDGET_CONFIG_LIST_CHANGED';
    public static WIDTH_CLASS_CHANGED = 'WIDTH_CLASS_CHANGED';
    public static USER_CHANGED = 'USER_CHANGED';
    public static MESSAGE_PUBLISHED = 'MESSAGE_PUBLISHED';
    public static PREFERRED_SETTINGS_CHANGED = 'PREFERRED_SETTINGS_CHANGED';
    public static PRINT_PREVIEW_MODE = 'PRINT_PREVIEW_MODE';
    public static TITLE_CHANGED = 'TITLE_CHANGED';
}

export class UrlConstants {
    public static SIDENAV_NAME = 'd.nav';
    public static SIDENAV_FILTER = 'd.filter';
    public static SIDENAV_SHOW = 'd.showSideNav';
    public static BREADCRUMB_SHOW = 'd.showBreadcrumb';
    public static WIDGETS_PER_ROW = 'd.perRow';
}

export class CacheConstants {
    public static DEFAULT = 'default';
    public static NEVER = 'never';
    public static PERMANENT = 'permanent';
}
